/**
 * Single source of truth for dashboard nav vs OrgRole capabilities.
 * Backend route policy maps domains the same way (see permissionV2RoutePolicy).
 *
 * Store version: bump AUTHORIZATION_STORE_VERSION to purge stale localStorage
 * after deploy (F6b/F6c). Old profiles without a matching version self-invalidate.
 * A purged empty store is a LOADING state — never a role verdict.
 */

export const AUTHORIZATION_STORE_VERSION = 3;
export const AUTHORIZATION_STORAGE_KEY = 'authorization';

export const AUTH_STATUS = Object.freeze({
  LOADING: 'LOADING',
  ERROR: 'ERROR',
  LOADED: 'LOADED'
});

const PLATFORM_SUPERUSER = 'platform.superuser';

const emptyAuthorization = () => ({
  systemRole: '',
  orgRoleId: null,
  orgRoleName: null,
  capabilities: [],
  storeVersion: AUTHORIZATION_STORE_VERSION
});

export const getAuthorization = () => {
  try {
    const raw = localStorage.getItem(AUTHORIZATION_STORAGE_KEY);
    if (!raw || raw === 'undefined') return emptyAuthorization();
    const parsed = JSON.parse(raw) || {};
    if (Number(parsed.storeVersion) !== AUTHORIZATION_STORE_VERSION) {
      localStorage.removeItem(AUTHORIZATION_STORAGE_KEY);
      return emptyAuthorization();
    }
    return {
      systemRole: parsed.systemRole || '',
      orgRoleId: parsed.orgRoleId || null,
      orgRoleName: parsed.orgRoleName || null,
      capabilities: Array.isArray(parsed.capabilities) ? parsed.capabilities : [],
      storeVersion: AUTHORIZATION_STORE_VERSION
    };
  } catch (_error) {
    return emptyAuthorization();
  }
};

export const setAuthorization = (authorization) => {
  if (!authorization) {
    localStorage.removeItem(AUTHORIZATION_STORAGE_KEY);
    return;
  }
  localStorage.setItem(
    AUTHORIZATION_STORAGE_KEY,
    JSON.stringify({
      systemRole: authorization.systemRole || '',
      orgRoleId: authorization.orgRoleId || null,
      orgRoleName: authorization.orgRoleName || null,
      capabilities: Array.isArray(authorization.capabilities)
        ? authorization.capabilities
        : [],
      storeVersion: AUTHORIZATION_STORE_VERSION
    })
  );
};

export const clearAuthorization = () => {
  localStorage.removeItem(AUTHORIZATION_STORAGE_KEY);
  // Purge legacy dual-write keys so old profiles cannot feed nav/permissions.
  localStorage.removeItem('userPermissions');
  localStorage.removeItem('supportsNewPermission');
};

/** hasRole — OrgRole SoT: any capability (incl. platform.superuser / orgAdmin catalog). */
export const hasRole = (authorization = getAuthorization()) => {
  const caps = authorization?.capabilities;
  return Array.isArray(caps) && caps.length > 0;
};

/**
 * Derive legacy module→action map from capabilities for in-page button checks.
 * Replaces RolePermission.permissions reads (F6c SoT).
 */
export const permissionsMapFromCapabilities = (capabilities = []) => {
  const permissions = {};
  for (const capability of capabilities || []) {
    const dot = String(capability).indexOf('.');
    if (dot <= 0) continue;
    const domain = capability.slice(0, dot);
    const action = capability.slice(dot + 1);
    if (!domain || !action || domain === 'platform') continue;
    if (!permissions[domain]) permissions[domain] = {};
    permissions[domain][action] = true;
  }
  return permissions;
};

export const hasCapability = (capability, authorization = getAuthorization()) => {
  if (!capability) return true;
  const caps = authorization.capabilities;
  if (!Array.isArray(caps)) return false;
  if (caps.includes(capability)) return true;
  // platform.superuser satisfies any carrier capability (audit on API; FE mirrors).
  if (caps.includes(PLATFORM_SUPERUSER) && capability !== PLATFORM_SUPERUSER) {
    return true;
  }
  return false;
};

export const hasAnyCapability = (
  capabilities = [],
  authorization = getAuthorization()
) => {
  if (!capabilities.length) return true;
  return capabilities.some((capability) =>
    hasCapability(capability, authorization)
  );
};

/**
 * Decide whether a dashboard route is visible / accessible.
 * Prefer requiredCapability (canonical). requiresSystemRole is for admin-only pages.
 */
export const canAccessDashboardRoute = (
  route,
  authorization = getAuthorization()
) => {
  if (!route) return false;
  if (route.requiresSystemRole) {
    return authorization.systemRole === route.requiresSystemRole;
  }
  if (route.requiredCapability) {
    return hasCapability(route.requiredCapability, authorization);
  }
  if (Array.isArray(route.requiredAnyCapabilities)) {
    return hasAnyCapability(route.requiredAnyCapabilities, authorization);
  }
  // Deny-by-default for dashboard routes: undeclared capability = hidden.
  return false;
};

export const visibleDashboardRoutes = (
  routes = [],
  authorization = getAuthorization()
) => routes.filter((route) => canAccessDashboardRoute(route, authorization));

/** First allowed route in declared nav order (deterministic). */
export const firstAllowedRoute = (
  routes = [],
  authorization = getAuthorization()
) => visibleDashboardRoutes(routes, authorization)[0] || null;

export const firstAllowedPath = (
  routes = [],
  basePath = '/enhancedDashboard',
  authorization = getAuthorization()
) => {
  const route = firstAllowedRoute(routes, authorization);
  return route ? `${basePath}/${route.id}` : '';
};

export const mohitDispatchFixture = Object.freeze({
  systemRole: 'member',
  storeVersion: AUTHORIZATION_STORE_VERSION,
  capabilities: [
    'facilities.view',
    'fleetHealth.view',
    'history.view',
    'loads.add',
    'loads.edit',
    'loads.view'
  ]
});

/** Expected mohit sidebar ids after F6b (KAV) — declared nav order titles separately. */
export const mohitExpectedNavIds = Object.freeze([
  'facilities',
  'fleetHealth',
  'llama',
  'loadHistory',
  'my_loads'
]);

/** Declared nav order for dispatch KAV: My Loads, Facilities, Load History, Fleet Health */
export const mohitExpectedNavTitlesInOrder = Object.freeze([
  'My Loads',
  'Llama',
  'Facilities',
  'Load History',
  'Fleet Health'
]);
